import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Chip, Icon, LinearProgress, Pagination, Paper, Snackbar, SnackbarCloseReason, Table, TableBody,
  TableCell, TableContainer, TableFooter, TableHead, TableRow, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { FerramentasDaListagemNfe, LinhasEsqueletoTabela } from '../../../shared/components';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { useDebounce } from '../../../shared/hooks';
import { Environment } from '../../../shared/environment';
import { getRowsPerPageCache, setRowsPerPageCache } from '../../../shared/utils/paginaRowsCache';
import { IListagemMdfe, MdfeService, situacaoMdfe, TSituacaoMdfe, TTipoBuscaMdfe } from '../../../shared/services/api/Fiscal/MdfeService';
import { CelulaListaMobile, LinhaListagemMobile, ListaMobile, useCelular, moedaMobile } from '../../../shared/components/listagem-mobile';

// Listagem de MDF-e (Fiscal > MDF-e) — mesmo padrão visual da listagem de NF-e
// (ListagemDeNfe.tsx): busca por data de emissão, número ou contratante,
// paginação ajustada à altura da tela. Mostra os campos que o GET /mdfe
// devolve (ver MdfeService.ts). "Adicionar" abre o Novo MDF-e (DetalheDeMdfe).
const OPCOES_BUSCA: { valor: TTipoBuscaMdfe; rotulo: string }[] = [
  { valor: 'm.dhemi', rotulo: 'Data Emissão' },
  { valor: 'n.nfe', rotulo: 'Número' },
  { valor: 'p.nome', rotulo: 'Contratante' },
];

const SITUACOES: Record<TSituacaoMdfe, { label: string; cor: 'info' | 'success' | 'error' | 'default'; icone: string }> = {
  pendente: { label: 'Pendente', cor: 'info', icone: 'pending' },
  autorizado: { label: 'Autorizado', cor: 'success', icone: 'check_circle' },
  cancelado: { label: 'Cancelado', cor: 'error', icone: 'cancel' },
  encerrado: { label: 'Encerrado', cor: 'default', icone: 'flag' },
};

const formatarCpfCnpj = (valor: string | null | undefined) => {
  const numeros = (valor || '').replace(/\D/g, '');
  if (numeros.length === 11) return numeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (numeros.length === 14) return numeros.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return valor || '';
};

const ROWS_CACHE_KEY = 'mdfe';
const ALTURA_LINHA = 50;
const FOOTER_RESERVADO = 56;

export const ListagemDeMdfe: React.FC = () => {
  const listaCelular = useCelular();
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { debounce } = useDebounce();
  const [rows, setRows] = useState<IListagemMdfe[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [rowsPerPage, setRowsPerPage] = useState(() => getRowsPerPageCache(ROWS_CACHE_KEY) ?? Environment.LIMITE_DE_LINHASSMALL);
  const [availableHeight, setAvailableHeight] = useState<number>();
  const [containerWidth, setContainerWidth] = useState(0);
  const [erro, setErro] = useState('');

  const tipo = useMemo(() => (searchParams.get('tipo') as TTipoBuscaMdfe) || 'm.dhemi', [searchParams]);
  // Na busca por data, abre já com o dia de hoje. Só quando a URL não traz
  // `busca` — se o usuário limpar a data (busca=''), continua vazio (todos).
  const busca = useMemo(() => {
    if (searchParams.has('busca')) return searchParams.get('busca') || '';
    return tipo === 'm.dhemi' ? dayjs().format('YYYY-MM-DD') : '';
  }, [searchParams, tipo]);
  const pagina = useMemo(() => Number(searchParams.get('pagina') || '1'), [searchParams]);

  useEffect(() => {
    setIsLoading(true);
    debounce(() => {
      MdfeService.getAll(pagina, busca, tipo, rowsPerPage).then((result) => {
        setIsLoading(false);
        if (result instanceof Error) {
          setErro(result.message);
          return;
        }
        setRows(result.data);
        setTotalCount(result.totalCount);
      });
    });
  }, [busca, tipo, pagina, rowsPerPage]);

  // Quantas linhas cabem até o rodapé da janela (mesma conta da listagem de NF-e).
  const calcularLimite = () => {
    const container = containerRef.current;
    if (!container) return;
    const alturaContainer = Math.max(200, window.innerHeight - container.getBoundingClientRect().top - 16);
    const alturaHeader = container.querySelector('thead')?.getBoundingClientRect().height ?? 34;
    const novoLimite = Math.min(100, Math.max(1, Math.floor((alturaContainer - alturaHeader - FOOTER_RESERVADO) / ALTURA_LINHA) - 1));
    setAvailableHeight(alturaContainer);
    setRowsPerPageCache(ROWS_CACHE_KEY, novoLimite);
    setRowsPerPage(novoLimite);
  };
  useLayoutEffect(() => { calcularLimite(); }, []);
  useEffect(() => {
    window.addEventListener('resize', calcularLimite);
    return () => window.removeEventListener('resize', calcularLimite);
  }, []);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => entries.forEach((entry) => setContainerWidth(entry.contentRect.width)));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Colunas que somem em telas estreitas (celular / menu lateral aberto).
  const largo = containerWidth === 0 || containerWidth >= 560;

  const mudarFiltros = (novo: { busca?: string; tipo?: string; pagina?: number }) =>
    setSearchParams({ busca: novo.busca ?? busca, tipo: novo.tipo ?? tipo, pagina: String(novo.pagina ?? 1) }, { replace: true });

  const colunas = 5 + (largo ? 1 : 0);

  return (
    <LayoutBaseDePagina
      titulo="Listagem de MDF-e"
      barraDeFerramentas={
        <FerramentasDaListagemNfe
          opcoesSelect={OPCOES_BUSCA}
          mostrarInputBasicSelect
          mostrarInputBusca={tipo !== 'm.dhemi'}
          mostrarInputData={tipo === 'm.dhemi'}
          textoDoSelect={tipo}
          textoDaBusca={busca}
          textoDoData={tipo === 'm.dhemi' && busca ? dayjs(busca, 'YYYY-MM-DD') : null}
          textoBotaoNovo="Adicionar"
          aoClicarEmNovo={() => navigate('/mdfe/novo')}
          aoMudarTextoDoSelect={(novoTipo) => mudarFiltros({ tipo: novoTipo, busca: novoTipo === 'm.dhemi' ? dayjs().format('YYYY-MM-DD') : '' })}
          aoMudarTextoDeBusca={(texto) => mudarFiltros({ busca: texto })}
          aoMudarData={(data) => mudarFiltros({ busca: data && data.isValid() ? data.format('YYYY-MM-DD') : '' })}
        />
      }
    >
      <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={!!erro} onClose={(_e?: React.SyntheticEvent | Event, motivo?: SnackbarCloseReason) => motivo !== 'clickaway' && setErro('')}>
        <Alert onClose={() => setErro('')} severity="error" variant="filled" sx={{ width: '100%' }}>{erro}</Alert>
      </Snackbar>

      <TableContainer
        ref={containerRef}
        component={Paper}
        variant="outlined"
        sx={{
          ml: { xs: 1, sm: 2 }, mt: 1, mb: 1, borderRadius: 2.5, overflow: 'auto',
          maxHeight: availableHeight ? `${availableHeight}px` : 'calc(100vh - 240px)',
          width: { xs: 'calc(100% - 8px)', sm: 'calc(99% - 16px)' },
          boxShadow: theme.shadows[2],
        }}
      >
        <Table size="medium">
          {!listaCelular && (
          <TableHead
            sx={{
              position: 'sticky', top: 0, zIndex: 2,
              '& th': {
                fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
                color: theme.palette.text.secondary, backgroundColor: alpha(theme.palette.primary.main, 0.06), padding: '10px 8px',
              },
            }}
          >
            <TableRow>
              <TableCell align="center">Emissão</TableCell>
              <TableCell align="right">Nº</TableCell>
              <TableCell>Contratante</TableCell>
              {largo && <TableCell>CPF / CNPJ</TableCell>}
              <TableCell align="right">Valor da carga</TableCell>
              <TableCell align="right">Situação</TableCell>
            </TableRow>
          </TableHead>
          )}

          {isLoading && rows.length === 0 && (
            <TableBody>
              <LinhasEsqueletoTabela
                colunas={Array.from({ length: colunas }, () => ({ width: '70%' }))}
              />
            </TableBody>
          )}

          <TableBody>
            {!isLoading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={colunas} align="center">
                  <Box py={4} display="flex" flexDirection="column" alignItems="center" gap={1}>
                    <Icon sx={{ fontSize: 40, color: theme.palette.text.secondary }}>local_shipping</Icon>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      {busca ? 'Nenhum MDF-e encontrado com esse filtro' : 'Nenhum MDF-e emitido ainda'}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {listaCelular ? (
              <CelulaListaMobile>
                <ListaMobile
                  linhas={rows}
                  chave={(row) => row.idmdfe}
                  renderizar={(row) => (
                    <LinhaListagemMobile
                      avatar={row.nome || '?'}
                      titulo={row.nome || 'Sem contratante'}
                      detalhes={[dayjs(row.dhemi).format('DD/MM/YY'), `Nº ${row.nmdf}`]}
                      valor={moedaMobile(row.vcarga)}
                      etiqueta={{ rotulo: SITUACOES[situacaoMdfe(row.status)].label, cor: SITUACOES[situacaoMdfe(row.status)].cor }}
                      onClick={() => navigate(`/mdfe/${row.idmdfe}`)}
                    />
                  )}
                />
              </CelulaListaMobile>
            ) : rows.map((row) => {
              const situacao = SITUACOES[situacaoMdfe(row.status)];
              return (
                <TableRow key={row.idmdfe} hover onClick={() => navigate(`/mdfe/${row.idmdfe}`)} sx={{ cursor: 'pointer' }}>
                  <TableCell align="center" sx={{ fontSize: '0.75rem' }}>{dayjs(row.dhemi).format('DD/MM/YYYY')}</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.75rem', fontFamily: 'monospace', fontWeight: 700, color: theme.palette.primary.main }}>
                    {row.nmdf}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <Tooltip title={row.nome || ''} arrow><span>{row.nome || '-'}</span></Tooltip>
                  </TableCell>
                  {largo && <TableCell sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>{formatarCpfCnpj(row.cpf)}</TableCell>}
                  <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 700, color: theme.palette.success.dark, whiteSpace: 'nowrap' }}>
                    {Number(row.vcarga || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell align="right">
                    <Chip size="small" color={situacao.cor} label={situacao.label}
                      icon={<Icon sx={{ fontSize: 14 }}>{situacao.icone}</Icon>} sx={{ height: 26, fontSize: '0.7rem', fontWeight: 600 }} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>

          <TableFooter>
            {isLoading && (
              <TableRow><TableCell colSpan={colunas}><LinearProgress /></TableCell></TableRow>
            )}
            {totalCount > rowsPerPage && (
              <TableRow>
                <TableCell colSpan={colunas}>
                  <Box display="flex" justifyContent="center" py={2}>
                    <Pagination
                      page={pagina}
                      count={Math.ceil(totalCount / rowsPerPage)}
                      onChange={(_, novaPagina) => mudarFiltros({ pagina: novaPagina })}
                      size="small" color="primary" showFirstButton showLastButton
                    />
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableFooter>
        </Table>
      </TableContainer>
    </LayoutBaseDePagina>
  );
};
